import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, useController } from 'react-hook-form';
import { Box, RadioGroup, FormControlLabel, Radio } from 'edenred-ui';
import { useTranslation } from 'react-i18next';

import { FundsLoadingTypes, Schedule } from '@epi-constants/benefitTypes';
import { FormFields, createRules } from '@epi-forms/helpers';
import { required } from '@epi-helpers/formHelpers/validators';
import {
  today,
  December,
  TOMORROW,
  FIRST_DAY_OF_NEXT_MONTH,
  END_OF_NEXT_YEAR,
  END_OF_YEAR,
  isTomorrow
} from '@epi-helpers/TopupHelper';

import { TerminationDatePicker } from '@epi-pages/Beneficiaries/components/TerminationDate/components/TerminationDatePicker';
import {
  getCurrentLunchFundsLoadingType,
  getIsLunchFundsLoadingTypePending
} from '@epi-selectors/settings';

const scheduleType = FormFields.ScheduleType;
const scheduleDate = FormFields.ScheduleDate;

export const TopupSchedule = () => {
  const { control, setValue } = useFormContext();
  const { t } = useTranslation();
  const validators = [required];
  const currentLunchFundsLoadingType = useSelector(
    getCurrentLunchFundsLoadingType
  );
  const isLunchFundsLoadingTypePending = useSelector(
    getIsLunchFundsLoadingTypePending
  );
  const disabledCustomDatesStandard =
    today > December
      ? { before: TOMORROW, after: END_OF_NEXT_YEAR }
      : { before: TOMORROW, after: END_OF_YEAR };

  const disabledBeforeFirstDayOfNewMonth =
    today > December
      ? { before: FIRST_DAY_OF_NEXT_MONTH, after: END_OF_NEXT_YEAR }
      : { before: FIRST_DAY_OF_NEXT_MONTH, after: END_OF_YEAR };

  const isDirectDisabled =
    isLunchFundsLoadingTypePending &&
    currentLunchFundsLoadingType === FundsLoadingTypes.TopUpRequest;

  const {
    field: { value: valueType, onChange }
  } = useController({
    name: scheduleType,
    control
  });

  const {
    field: { value: valueDate, onChange: dateOnChange },
    fieldState: { error }
  } = useController({
    name: scheduleDate,
    control,
    rules: createRules(valueType === Schedule.SelectDate ? validators : [])
  });

  const isError = error ? !!error : valueDate && !isTomorrow(valueDate);

  useEffect(() => {
    if (isDirectDisabled) {
      setValue(scheduleType, Schedule.SelectDate);
      setValue(scheduleDate, FIRST_DAY_OF_NEXT_MONTH);
    }
  }, [isDirectDisabled]);

  const handleLoadDateTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;

    if (value === Schedule.SelectDate) {
      if (isDirectDisabled) {
        setValue(scheduleDate, FIRST_DAY_OF_NEXT_MONTH);
      } else {
        setValue(scheduleDate, TOMORROW);
      }
    } else {
      setValue(scheduleDate, '');
    }

    onChange(value);
  };

  return (
    <Box fontSize={16} fontWeight={400} position="relative" top={3}>
      <RadioGroup onChange={handleLoadDateTypeChange} value={valueType}>
        <Box display="flex" gap={1} flexDirection="column">
          <FormControlLabel
            value={Schedule.Direct}
            control={<Radio />}
            label={t('containers.deposit_money.topup_tab.form.schedule.direct')}
            id="choose-as-direct"
            disabled={isDirectDisabled}
          />
          <FormControlLabel
            value={Schedule.SelectDate}
            control={<Radio />}
            label={t(
              'containers.deposit_money.topup_tab.form.schedule.select_date'
            )}
            id="choose-as-select-date"
          />
        </Box>
      </RadioGroup>
      {valueType === Schedule.SelectDate ? (
        <Box display="flex" flexDirection="row" alignItems="end">
          <TerminationDatePicker
            dayValue={valueDate}
            setTerminationDate={dateOnChange}
            isError={isError}
            title={t(
              'containers.deposit_money.topup_tab.form.schedule.select_date_title'
            )}
            disabledCustomDates={
              isDirectDisabled
                ? disabledBeforeFirstDayOfNewMonth
                : disabledCustomDatesStandard
            }
            errorText={t(
              'containers.deposit_money.topup_tab.form.schedule.error_text'
            )}
          />
        </Box>
      ) : null}
    </Box>
  );
};
